import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchDebugControllerSnapshot,
  setBlockedPages,
  setDebugSiteMode,
  wipeAllDebugData,
  wipeAuthDebugTable,
  wipeDebugTable,
} from '../../api/debugController';
import HawbCronDebugPanel from './HawbCronDebugPanel';
import {
  Badge,
  ConfirmDialog,
  PageHeader,
  PanelHeader,
  StatusBanner,
  inputClassName,
  labelClassName,
  panelClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
  tableCellClassName,
  tableHeadClassName,
} from '../../components/ui';

const MODE_TONES = {
  all_good: 'success',
  limited_usage: 'warning',
  under_maintenance: 'danger',
};

const DEBUG_NAV = [
  {
    id: 'site',
    label: 'Site control',
    description: 'Mode, blocks, wipe',
  },
  {
    id: 'hawb-cron',
    label: 'HAWB cron',
    description: 'Backfill logs',
  },
];

function formatCount(value) {
  return new Intl.NumberFormat('en-US').format(value || 0);
}

export default function DebugControllerPage({ token, user, onSignOut, onSystemStatusChange }) {
  const [debugNav, setDebugNav] = useState('site');
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const [modeDraft, setModeDraft] = useState('all_good');
  const [messageDraft, setMessageDraft] = useState('');
  const [pendingWipeTable, setPendingWipeTable] = useState(null);
  const [pendingWipeAll, setPendingWipeAll] = useState(false);
  const [wipeAllConfirmText, setWipeAllConfirmText] = useState('');
  const [pendingAuthWipeTable, setPendingAuthWipeTable] = useState(null);
  const [authWipePassword, setAuthWipePassword] = useState('');
  const [blockedDraft, setBlockedDraft] = useState([]);

  const AUTH_WIPEABLE = useMemo(() => new Set(['admin', 'superadmin']), []);

  const loadSnapshot = useCallback(async () => {
    setLoading(true);
    setStatus({ type: 'idle', message: '' });

    try {
      const payload = await fetchDebugControllerSnapshot(token);
      setSnapshot(payload);
      setModeDraft(payload.status?.mode || 'all_good');
      setMessageDraft(payload.status?.message || '');
      setBlockedDraft(payload.blockedPages || payload.status?.blockedPages || []);
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to load debug controller.',
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (debugNav === 'site') {
      loadSnapshot();
    }
  }, [loadSnapshot, debugNav]);

  const wipeableTables = useMemo(
    () => (snapshot?.tables || []).filter((table) => table.wipeable),
    [snapshot]
  );
  const protectedTables = useMemo(
    () => (snapshot?.tables || []).filter((table) => !table.wipeable),
    [snapshot]
  );
  const totalWipeableRows = useMemo(
    () => wipeableTables.reduce((sum, table) => sum + (table.rowCount || 0), 0),
    [wipeableTables]
  );

  const handleSaveMode = async (event) => {
    event.preventDefault();
    setBusy(true);
    setStatus({ type: 'idle', message: '' });

    try {
      const payload = await setDebugSiteMode(token, {
        mode: modeDraft,
        message: messageDraft,
      });
      setSnapshot(payload.snapshot);
      if (payload.status && onSystemStatusChange) {
        onSystemStatusChange({
          mode: payload.status.mode,
          message: payload.status.message || '',
          blockedPages: payload.status.blockedPages || [],
        });
      }
      setStatus({ type: 'success', message: payload.message });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to update site mode.',
      });
    } finally {
      setBusy(false);
    }
  };

  const toggleBlockedPage = (pageId) => {
    setBlockedDraft((current) =>
      current.includes(pageId)
        ? current.filter((id) => id !== pageId)
        : [...current, pageId]
    );
  };

  const handleSaveBlockedPages = async (event) => {
    event.preventDefault();
    setBusy(true);
    setStatus({ type: 'idle', message: '' });

    try {
      const payload = await setBlockedPages(token, blockedDraft);
      setSnapshot(payload.snapshot);
      setBlockedDraft(payload.status?.blockedPages || payload.snapshot?.blockedPages || []);
      if (payload.status && onSystemStatusChange) {
        onSystemStatusChange({
          mode: payload.status.mode,
          message: payload.status.message || '',
          blockedPages: payload.status.blockedPages || [],
        });
      }
      setStatus({ type: 'success', message: payload.message });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to update blocked pages.',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleWipeTable = async () => {
    if (!pendingWipeTable) {
      return;
    }

    setBusy(true);
    setStatus({ type: 'idle', message: '' });

    try {
      const payload = await wipeDebugTable(token, pendingWipeTable.name);
      setSnapshot(payload.snapshot);
      setStatus({ type: 'success', message: payload.message });
      setPendingWipeTable(null);
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to wipe table.',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleWipeAll = async () => {
    if (wipeAllConfirmText.trim() !== 'WIPE ALL') {
      setStatus({
        type: 'error',
        message: 'Type WIPE ALL exactly to confirm the full data wipe.',
      });
      return;
    }

    setBusy(true);
    setStatus({ type: 'idle', message: '' });

    try {
      const payload = await wipeAllDebugData(token);
      setSnapshot(payload.snapshot);
      setStatus({ type: 'success', message: payload.message });
      setPendingWipeAll(false);
      setWipeAllConfirmText('');
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to wipe all data.',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleWipeAuthTable = async () => {
    if (!pendingAuthWipeTable) {
      return;
    }
    if (!authWipePassword.trim()) {
      setStatus({
        type: 'error',
        message: 'Enter the auth wipe password from backend .env.',
      });
      return;
    }

    setBusy(true);
    setStatus({ type: 'idle', message: '' });

    const tableName = pendingAuthWipeTable.name;

    try {
      const payload = await wipeAuthDebugTable(token, tableName, authWipePassword);
      setSnapshot(payload.snapshot);
      setStatus({ type: 'success', message: payload.message });
      setPendingAuthWipeTable(null);
      setAuthWipePassword('');
      if (tableName === 'admin' && typeof onSignOut === 'function') {
        onSignOut();
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to wipe auth table.',
      });
    } finally {
      setBusy(false);
    }
  };

  if (user?.role !== 'super_admin' && user?.role !== 'admin') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className={`${panelClassName} max-w-md p-6 text-center`}>
          <h1 className="text-lg font-semibold">Access denied</h1>
          <p className="crm-text-muted mt-2 text-sm">
            debug-controller-4719 is restricted to admin accounts.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[rgb(var(--crm-page-bg))] px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-6xl flex-col gap-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Internal ops
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight">
              debug-controller-4719
            </h1>
            <p className="crm-text-muted mt-1 text-sm">
              Debug-only tools — not shown in the main CRM sidebar.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {debugNav === 'site' ? (
              <button
                type="button"
                className={secondaryButtonClassName}
                onClick={loadSnapshot}
                disabled={loading || busy}
              >
                Refresh
              </button>
            ) : null}
            {onSignOut ? (
              <button type="button" className={secondaryButtonClassName} onClick={onSignOut}>
                Sign out
              </button>
            ) : null}
          </div>
        </div>

        <nav
          className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-white p-1.5 shadow-sm"
          aria-label="Debug sections"
        >
          {DEBUG_NAV.map((item) => {
            const selected = debugNav === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  setDebugNav(item.id);
                  setStatus({ type: 'idle', message: '' });
                }}
                className={[
                  'min-w-[9rem] flex-1 rounded-lg px-3 py-2.5 text-left transition',
                  selected
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
                ].join(' ')}
              >
                <span className="block text-sm font-semibold">{item.label}</span>
                <span
                  className={[
                    'mt-0.5 block text-[11px]',
                    selected ? 'text-slate-300' : 'text-slate-400',
                  ].join(' ')}
                >
                  {item.description}
                </span>
              </button>
            );
          })}
        </nav>

        {debugNav === 'hawb-cron' ? (
          <HawbCronDebugPanel token={token} />
        ) : (
          <>
        {status.type !== 'idle' ? <StatusBanner status={status} setStatus={setStatus} /> : null}

        <section className={panelClassName}>
          <PanelHeader
            title="Website status"
            subtitle="Switch between all good, limited usage, and under maintenance."
            action={
              snapshot?.status ? (
                <Badge tone={MODE_TONES[snapshot.status.mode] || 'default'}>
                  {snapshot.status.label}
                </Badge>
              ) : null
            }
          />
          <form className="space-y-4 px-5 pb-5" onSubmit={handleSaveMode}>
            <div className="grid gap-3 md:grid-cols-3">
              {(snapshot?.modes || [
                { id: 'all_good', label: 'All good', description: 'Fully operational.' },
                {
                  id: 'limited_usage',
                  label: 'Limited usage',
                  description: 'Restricted capacity messaging.',
                },
                {
                  id: 'under_maintenance',
                  label: 'Under maintenance',
                  description: 'Maintenance screen for operators.',
                },
              ]).map((mode) => {
                const selected = modeDraft === mode.id;
                return (
                  <label
                    key={mode.id}
                    className={[
                      'cursor-pointer rounded-lg border p-4 transition',
                      selected
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                        : 'border-[rgb(var(--crm-border))] bg-white hover:border-slate-300',
                    ].join(' ')}
                  >
                    <input
                      type="radio"
                      name="site-mode"
                      className="sr-only"
                      value={mode.id}
                      checked={selected}
                      onChange={() => setModeDraft(mode.id)}
                      disabled={busy || loading}
                    />
                    <strong className="block text-sm font-semibold">{mode.label}</strong>
                    <span className="crm-text-muted mt-1 block text-xs leading-relaxed">
                      {mode.description}
                    </span>
                  </label>
                );
              })}
            </div>

            <div>
              <label htmlFor="status-message" className={labelClassName}>
                Status message (shown to users)
              </label>
              <textarea
                id="status-message"
                className={`${inputClassName} mt-1 min-h-[88px]`}
                value={messageDraft}
                onChange={(event) => setMessageDraft(event.target.value)}
                placeholder="Optional note shown on maintenance / limited usage screens"
                maxLength={500}
                disabled={busy || loading}
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="crm-text-muted text-xs">
                {snapshot?.status?.updatedAt
                  ? `Last updated ${new Date(snapshot.status.updatedAt).toLocaleString()}`
                  : loading
                    ? 'Loading…'
                    : 'No updates yet'}
              </p>
              <button
                type="submit"
                className={primaryButtonClassName}
                disabled={busy || loading}
              >
                {busy ? 'Saving…' : 'Save site mode'}
              </button>
            </div>
          </form>
        </section>

        <section className={panelClassName}>
          <PanelHeader
            title="Block pages"
            subtitle="Blocked pages show “Work is under progress” to every admin user."
            action={
              blockedDraft.length ? (
                <Badge tone="warning">{blockedDraft.length} blocked</Badge>
              ) : (
                <Badge tone="success">None blocked</Badge>
              )
            }
          />
          <form className="space-y-4 px-5 pb-5" onSubmit={handleSaveBlockedPages}>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {(snapshot?.blockablePages || []).map((page) => {
                const checked = blockedDraft.includes(page.id);
                return (
                  <label
                    key={page.id}
                    className={[
                      'flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-3 transition',
                      checked
                        ? 'border-amber-300 bg-amber-50'
                        : 'border-[rgb(var(--crm-border))] bg-white hover:border-slate-300',
                    ].join(' ')}
                  >
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-slate-300"
                      checked={checked}
                      onChange={() => toggleBlockedPage(page.id)}
                      disabled={busy || loading}
                    />
                    <span className="min-w-0">
                      <strong className="block text-sm font-semibold text-slate-900">
                        {page.label}
                      </strong>
                      <span className="crm-text-muted mt-0.5 block truncate text-xs">
                        {page.path}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>

            {!loading && !(snapshot?.blockablePages || []).length ? (
              <p className="crm-text-muted text-sm">No blockable pages loaded yet.</p>
            ) : null}

            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="crm-text-muted text-xs">
                Tip: leave Home unblocked so users can navigate away.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className={secondaryButtonClassName}
                  disabled={busy || loading || blockedDraft.length === 0}
                  onClick={() => setBlockedDraft([])}
                >
                  Unblock all
                </button>
                <button
                  type="submit"
                  className={primaryButtonClassName}
                  disabled={busy || loading}
                >
                  {busy ? 'Saving…' : 'Save page blocks'}
                </button>
              </div>
            </div>
          </form>
        </section>

        <section className={panelClassName}>
          <PanelHeader
            title="Wipe all business data"
            subtitle={`${formatCount(totalWipeableRows)} rows across ${wipeableTables.length} wipeable tables. Admin / superadmin accounts are kept.`}
            action={
              <button
                type="button"
                className="inline-flex min-h-10 items-center justify-center rounded-md bg-red-600 px-4 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => {
                  setWipeAllConfirmText('');
                  setPendingWipeAll(true);
                }}
                disabled={busy || loading}
              >
                Wipe all data
              </button>
            }
          />
          <div className="px-5 pb-5">
            <p className="crm-text-muted text-sm leading-relaxed">
              This truncates orders, products, inventory, procurement, invoices, sync jobs,
              activity logs, and related tables. Auth accounts and the system_control row stay.
            </p>
          </div>
        </section>

        <section className="space-y-3">
          <PageHeader
            title="Tables"
            subtitle="Delete a single table. admin/superadmin need the wipe password; system_control stays protected."
          />

          <div className={`${panelClassName} overflow-x-auto`}>
            <table className="w-full min-w-[40rem] border-collapse">
              <thead>
                <tr>
                  <th className={tableHeadClassName}>Table</th>
                  <th className={tableHeadClassName}>Rows</th>
                  <th className={tableHeadClassName}>Notes</th>
                  <th className={tableHeadClassName}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className={`${tableCellClassName} crm-text-muted text-center`}>
                      Loading table stats…
                    </td>
                  </tr>
                ) : (
                  <>
                    {wipeableTables.map((table) => (
                      <tr key={table.name}>
                        <td className={tableCellClassName}>
                          <code className="text-sm font-semibold">{table.name}</code>
                        </td>
                        <td className={tableCellClassName}>{formatCount(table.rowCount)}</td>
                        <td className={`${tableCellClassName} crm-text-muted`}>
                          {table.description}
                        </td>
                        <td className={tableCellClassName}>
                          <button
                            type="button"
                            className="text-sm font-semibold text-red-600 hover:text-red-700 disabled:opacity-50"
                            onClick={() => setPendingWipeTable(table)}
                            disabled={busy || table.rowCount === 0}
                          >
                            Delete table data
                          </button>
                        </td>
                      </tr>
                    ))}
                    {protectedTables.map((table) => (
                      <tr key={table.name}>
                        <td className={tableCellClassName}>
                          <code className="text-sm font-semibold">{table.name}</code>
                        </td>
                        <td className={tableCellClassName}>{formatCount(table.rowCount)}</td>
                        <td className={`${tableCellClassName} crm-text-muted`}>
                          {table.description}
                        </td>
                        <td className={tableCellClassName}>
                          {AUTH_WIPEABLE.has(table.name) ? (
                            <button
                              type="button"
                              className="text-sm font-semibold text-red-600 hover:text-red-700 disabled:opacity-50"
                              onClick={() => {
                                setAuthWipePassword('');
                                setPendingAuthWipeTable(table);
                              }}
                              disabled={busy || table.rowCount === 0}
                            >
                              Delete with password
                            </button>
                          ) : (
                            <Badge tone="default">Protected</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </section>
          </>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(pendingWipeTable)}
        title={`Wipe ${pendingWipeTable?.name}?`}
        message={`This permanently deletes ${formatCount(pendingWipeTable?.rowCount)} row(s) from ${pendingWipeTable?.name}. Related child rows may also be removed via CASCADE.`}
        confirmLabel="Wipe table"
        confirmingLabel="Wiping…"
        cancelLabel="Cancel"
        tone="danger"
        confirming={busy}
        onConfirm={handleWipeTable}
        onCancel={() => {
          if (!busy) {
            setPendingWipeTable(null);
          }
        }}
      />

      {pendingAuthWipeTable ? (
        <div
          className="fixed inset-0 z-[70] grid place-items-center bg-slate-950/55 p-4 backdrop-blur-[2px]"
          role="presentation"
          onClick={
            busy
              ? undefined
              : () => {
                  setPendingAuthWipeTable(null);
                  setAuthWipePassword('');
                }
          }
        >
          <div
            className="crm-panel w-full max-w-md overflow-hidden shadow-2xl"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-6 pb-4 pt-6">
              <h2 className="text-lg font-semibold">
                Wipe {pendingAuthWipeTable.name}?
              </h2>
              <p className="crm-text-muted mt-2 text-sm leading-relaxed">
                Deletes {formatCount(pendingAuthWipeTable.rowCount)} auth row(s).
                {pendingAuthWipeTable.name === 'admin'
                  ? ' This also CASCADE-deletes related business data (orders, listings, etc.) and signs you out.'
                  : ' Enter the password from DEBUG_CONTROLLER_AUTH_WIPE_PASSWORD.'}
              </p>
              <label htmlFor="auth-wipe-password" className={`${labelClassName} mt-4`}>
                Wipe password
              </label>
              <input
                id="auth-wipe-password"
                type="password"
                className={`${inputClassName} mt-1`}
                value={authWipePassword}
                onChange={(event) => setAuthWipePassword(event.target.value)}
                placeholder="Password from .env"
                disabled={busy}
                autoComplete="off"
              />
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))] px-6 py-4">
              <button
                type="button"
                className={secondaryButtonClassName}
                onClick={() => {
                  setPendingAuthWipeTable(null);
                  setAuthWipePassword('');
                }}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex min-h-10 items-center justify-center rounded-md bg-red-600 px-4 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleWipeAuthTable}
                disabled={busy || !authWipePassword.trim()}
              >
                {busy ? 'Wiping…' : 'Wipe auth table'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {pendingWipeAll ? (
        <div
          className="fixed inset-0 z-[70] grid place-items-center bg-slate-950/55 p-4 backdrop-blur-[2px]"
          role="presentation"
          onClick={busy ? undefined : () => setPendingWipeAll(false)}
        >
          <div
            className="crm-panel w-full max-w-md overflow-hidden shadow-2xl"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-6 pb-4 pt-6">
              <h2 className="text-lg font-semibold">Wipe all business data?</h2>
              <p className="crm-text-muted mt-2 text-sm leading-relaxed">
                This removes about {formatCount(totalWipeableRows)} rows across all wipeable
                tables. Type <strong>WIPE ALL</strong> to confirm.
              </p>
              <label htmlFor="wipe-all-confirm" className={`${labelClassName} mt-4`}>
                Confirmation
              </label>
              <input
                id="wipe-all-confirm"
                className={`${inputClassName} mt-1`}
                value={wipeAllConfirmText}
                onChange={(event) => setWipeAllConfirmText(event.target.value)}
                placeholder="WIPE ALL"
                disabled={busy}
                autoComplete="off"
              />
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))] px-6 py-4">
              <button
                type="button"
                className={secondaryButtonClassName}
                onClick={() => setPendingWipeAll(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex min-h-10 items-center justify-center rounded-md bg-red-600 px-4 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleWipeAll}
                disabled={busy || wipeAllConfirmText.trim() !== 'WIPE ALL'}
              >
                {busy ? 'Wiping…' : 'Wipe everything'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
